import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit, inject, ViewChild, ElementRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from '../../../core/services/auth.service';
import { User } from '../../../core/models/user.interface';
import { USER_TYPES } from '../../../core/shared/constants';
import { UsersService } from '../../../core/services/users.service';
import { AppointmentsService } from '../../../core/services/appointments.service';
import { EmailService, EmailData } from '../../../core/services/email.service';
import { LoaderService } from '../../../core/shared/services/loader.service';
import { MentorAvailabilityComponent } from "../mentors/mentor-availability/mentor-availability.component";
import { MatCalendar } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { TimeFormatPipe } from '../../../core/shared/pipes/time-format.pipe';
import { UsersAssignedTableComponent } from '../mentors/users-assigned-table/users-assigned-table.component';
import { CustomDatePickerComponent } from '../../../core/shared/components/custom-date-picker/custom-date-picker.component';
import { environment } from '../../../../environments/environment';
import Swal from 'sweetalert2';
import Quill from 'quill';

interface AppointmentFile {
  name: string;
  size: number;
  type: string;
  url: string;
  uploadDate: Date;
}

interface Session {
  id: string;
  menteeName: string;
  menteeAvatar?: string;
  date: Date;
  time: string;
  duration: number;
  milestone: string;
  status: 'pending' | 'completed' | 'cancelled';
  sessionUrl?: string;
  notes?: string;
  files?: AppointmentFile[];
}

interface UserDisplay {
  id: string;
  _id: string;
  name: string;
  avatar?: string;
  status: 'activo' | 'inactivo' | 'pendiente';
  currentStage: string;
  pendingTasks: number;
  nextAppointment: string;
  progress: number;
  createdAt: string;
  email: string;
  firstName: string;
  lastName: string;
}

@Component({
  selector: 'app-dashboard-coach',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, MatIconModule, MatCalendar, 
    MatNativeDateModule, TimeFormatPipe, MentorAvailabilityComponent, UsersAssignedTableComponent, CustomDatePickerComponent],
  templateUrl: './dashboard-coach.component.html',
  styleUrl: './dashboard-coach.component.scss'
})
export class DashboardCoachComponent implements OnInit {
  private authService = inject(AuthService);
  private usersService = inject(UsersService); // Asumiendo que tienes un servicio para manejar usuarios
  private appointmentsService = inject(AppointmentsService);
  private emailService = inject(EmailService);
  private loader = inject(LoaderService);

  user: User | null = null;
  currentDate: Date = new Date();
  selectedDate: Date = new Date();
  
  // Datos para usuarios asignados
  assignedUsers: any[] = [];
  todayAppointments: any[] = [];

  // Propiedades para filtros de usuarios
  userSearchTerm: string = '';
  userStatusFilter: string = '';
  userStageFilter: string = '';
  userDateFromFilter: string = '';
  userDateToFilter: string = '';

  // Datos filtrados y paginados de usuarios
  filteredUsers: UserDisplay[] = [];
  paginatedUsers: UserDisplay[] = [];

  // Paginación de usuarios
  userCurrentPage: number = 1;
  userPageSize: number = 5; // Limitar a 4 usuarios
  userTotalPages: number = 1;

  // Ordenamiento de usuarios
  userSortField: string = 'name';
  userSortDirection: 'asc' | 'desc' = 'asc';
  
  // Modal de cita
  showAppointmentModal: boolean = false;
  selectedAppointment: any = null;

  // Datos para la sección de mensajes
  messages: any[] = [];
  selectedMessage: any = null;

  citas: any[] = []; // Citas del coach
  citasFiltradas: any[] = []; // Citas filtradas

  // Modal de reunión
  showMeetingModal: boolean = false;
  meetingId: string = '';
  accessCode: string = '';
  meetingLink: string = '';
  isLoadingAppointmentDetails: boolean = false;

  // Propiedades para filtros y paginación de sesiones
  searchTerm: string = '';
  statusFilter: string = '';
  stageFilter: string = '';
  dateFromFilter: Date | string | null = null;
  dateToFilter: Date | string | null = null;

  // Datos de sesiones
  sessions: Session[] = [];
  private assignedCustomersMap: Map<string, any> = new Map();
  filteredSessions: Session[] = [];
  paginatedSessions: Session[] = [];

  // Paginación
  currentPage: number = 1;
  pageSize: number = 5;
  totalPages: number = 1;

  // Ordenamiento
  sortField: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  // Utilidad para template
  Math = Math;

  // Propiedades para el modal de notas
  showNotesModal: boolean = false;
  appointmentNotes: string = '';
  appointmentFiles: AppointmentFile[] = [];
  isSavingNotes: boolean = false;
  showPreview: boolean = false;
  quillInstance: Quill | null = null;

  @ViewChild('quillEditor', { static: false }) quillEditor?: ElementRef;
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  constructor(private router: Router, private loaderService: LoaderService) { }

  ngOnInit(): void {
    this.user = this.authService.getUser();
    // console.log('Usuario actual:', this.user);
    // Verificar si el usuario es un coach
    if (!(this.isCoachUser() || this.isExpertUser())) {
      console.error('Este módulo es solo para usuarios con rol de Coach');
      return;
    }

    // this.loadActivity();
    // this.loadAssignedUsers();
    this.loadAppointments(); // Cargar citas del coach
    this.loadMessages();
    this.loadAssignedUsers();
    this.loadTodayAppointments();
    // this.updateCurrentMonth();
  }

  // updateCurrentMonth(): void {
  //   const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  //     'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  //   this.currentMonth = months[this.selectedDate.getMonth()];
  // }

  // previousMonth(): void {
  //   this.selectedDate = new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth() - 1, 1);
  //   this.updateCurrentMonth();
  // }

  // nextMonth(): void {
  //   this.selectedDate = new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth() + 1, 1);
  //   this.updateCurrentMonth();
  // }

  // loadActivity(): void {
  //   this.lastActivity = [
  //     { date: '2023-10-01', activity: 'Ikigai desbloqueado' },
  //     { date: '2023-10-02', activity: 'Ikigai desbloqueado' },
  //   ]
  // }


  // loadAssignedUsers(): void {
   
  //   this.usersService.find(1, '').subscribe({
  //     next: (response) => {
  //       if (response && response.data) {
  //         this.assignedUsers = response.data;
  //         this.filteredUsers = this.assignedUsers; // Inicialmente, los usuarios filtrados son todos los asignados
  //         console.log('this.assignedUsers:', this.assignedUsers);
  //         // this.filterUsers();
  //       }

  //     },
  //     error: (error) => {
  //       console.error('Error al cargar tests:', error);
  //     }
  //   });

  //   // this.filterUsers();
  // }

  loadAppointments(): void {
    // Obtener citas del coach desde el usuario actual
    if (this.user && this.user.coachAppointments) {
      // Ya tenemos las citas en el usuario
      this.citas = this.user.coachAppointments;
      console.log('Citas cargadas:', this.citas);
      this.cargarCitasPorFecha(this.selectedDate); // Cargar citas para la fecha actual
      this.loadSessions(); // Cargar sesiones para la tabla
    }
  }

  loadMessages(): void {
    // Datos de ejemplo para los mensajes
    this.messages = [
      {
        id: '1',
        sender: 'Juan Salazar Lopez',
        status: 'Abierto',
        content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis consequat',
        timestamp: 'Hace 5 minutos',
        read: false
      },
      {
        id: '2',
        sender: 'Marco Gonzales',
        status: 'Pendiente',
        content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis consequat',
        timestamp: '11:12',
        read: false
      },
      {
        id: '3',
        sender: 'María Torres',
        status: 'Pendiente',
        content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis consequat',
        timestamp: '10:15',
        read: false
      },
      {
        id: '4',
        sender: 'Juan Salazar Lopez',
        status: 'Resuelto',
        content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis consequat',
        timestamp: '21:15',
        read: true
      },
      {
        id: '5',
        sender: 'Sale Almendra',
        status: 'Resuelto',
        content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis consequat',
        timestamp: 'Ayer',
        read: true
      }
    ];
  }

  // filterUsers(): void {
  //   // this.filteredUsers = [...this.assignedUsers];
  //   // this.filteredUsers = this.assignedUsers;
  //   // console.log('Filtrando usuarios:', this.filteredUsers);  

  //   // Filtrar por tab
  //   if (this.activeTab === 'nuevos') {
  //     // Aquí podrías implementar un filtro de usuarios nuevos
  //     // Por ejemplo, filtrar usuarios con menos de 7 días en la plataforma
  //   }

  //   // Ordenar
  //   if (this.sortOrder === 'fecha-ingreso') {
  //     // Ordenar por fecha de ingreso (simulado)
  //     console.log('Ordenando por fecha de ingreso');
  //     this.filteredUsers.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  //   } else if (this.sortOrder === 'nombre') {
  //     this.filteredUsers.sort((a, b) => a.name.localeCompare(b.name));
  //   } else if (this.sortOrder === 'ultima-actividad') {
  //     // Ordenar por última actividad (simulado)
  //   }
  // }

  // searchUsers(): void {
  //   // this.filterUsers();
  //   // Filtrar por búsqueda
  //   if (this.searchQuery.length > 0 && this.searchQuery.trim() !== '') {
  //     // this.filteredUsers = this.filteredUsers.filter(user =>
  //     //   user.name.toLowerCase().includes(this.searchQuery.toLowerCase())
  //     // );
  //     this.usersService.find(1, this.searchQuery).subscribe({
  //       next: (response) => {
  //         if (response && response.data) {
  //           this.assignedUsers = response.data;
  //           this.filteredUsers = this.assignedUsers;
  //           // this.filterUsers();
  //         }
  //         console.log('this.assignedUsers FILTER:', this.assignedUsers);
  //       },
  //       error: (error) => {
  //         console.error('Error al cargar tests:', error);
  //       }
  //     });
  //   } else {
  //     // Si no hay búsqueda, mostrar todos los usuarios asignados
  //     this.usersService.find(1, '').subscribe({
  //       next: (response) => {
  //         if (response && response.data) {
  //           this.assignedUsers = response.data;
  //           this.filteredUsers = this.assignedUsers;
  //           // this.filterUsers();
  //         }
  //         console.log('this.assignedUsers ALL:', this.assignedUsers);
  //       },
  //       error: (error) => {
  //         console.error('Error al cargar tests:', error);
  //       }
  //     });
  //   }
  // }


  loadMoreUsers(): void {
    // Implementación para cargar más usuarios
    console.log('Cargar más usuarios');
  }

  selectMessage(message: any): void {
    this.selectedMessage = message;
    // Marcar como leído
    message.read = true;
  }

  sendMessage(content: string): void {
    if (!content.trim()) return;

    // Aquí implementarías el envío de mensaje a través de un servicio
    console.log('Mensaje enviado:', content);

    // Limpiar el campo después de enviar
    // this.newMessageContent = '';
  }

  // Método para verificar si el usuario es Coach
  isCoachUser(): boolean {
    if (!this.user) return false;

    // Verificar por el tipo de usuario
    if (this.user.type && this.user.type.id === USER_TYPES.Coach.id) {
      return true;
    }

    // O verificar si tiene el rol en el array de roles

    if (
      this.user.roles &&
      Array.isArray(this.user.roles) &&
      this.user.roles.length > 0
    ) {
      return this.user.roles.some(
        (role) => typeof role === "object" && role && role.name === "Coach"
      );
    }

    return false;
  }

  isExpertUser(): boolean {
    if (!this.user) return false;

    // Verificar por el tipo de usuario
    if (this.user.type && this.user.type.id === USER_TYPES.Expert.id) {
      return true;
    }

    // O verificar si tiene el rol en el array de roles
    if (
      this.user.roles &&
      Array.isArray(this.user.roles) &&
      this.user.roles.length > 0
    ) {
      return this.user.roles.some(
        (role) => typeof role === "object" && role && role.name === "Experto"
      );
    }

    return false;
  }

  // Método para manejar el cambio de fecha en el calendario
  onDateChange(date: Date) {
    // console.log('Fecha seleccionada:', date);
    // const fechaSeleccionada = formatDate(date, 'yyyy-MM-dd', 'en-US');
    // console.log('Fecha formateada:', fechaSeleccionada);
    // this.citasFiltradas = this.citas.filter(cita => cita.date === fechaSeleccionada);
    // console.log('Citas filtradas para la fecha seleccionada:', this.citasFiltradas);
    this.cargarCitasPorFecha(date);
  }

  cargarCitasPorFecha(fecha: Date): void {
    // console.log('Fecha seleccionada:', date);
    const fechaSeleccionada = formatDate(fecha, 'yyyy-MM-dd', 'en-US');
    console.log('Fecha formateada:', fechaSeleccionada);
    this.citasFiltradas = this.citas.filter(cita => cita.date === fechaSeleccionada);
    console.log('Citas filtradas para la fecha seleccionada:', this.citasFiltradas);
  }

  // Función para eliminar una cita
  // Métodos para el modal de reunión
  openMeetingModal(appointment: any): void {
    console.log('Abriendo modal de reunión para appointment:', appointment);
    
    // Mostrar loading state
    this.isLoadingAppointmentDetails = true;
    this.showMeetingModal = true;
    
    // Obtener el ID del appointment
    const appointmentId = appointment.appointmentId || appointment.id;
    
    if (!appointmentId) {
      console.error('No se encontró ID del appointment');
      this.isLoadingAppointmentDetails = false;
      return;
    }
    
    // Cargar detalles completos del appointment desde el backend
    this.appointmentsService.findById(appointmentId).subscribe({
      next: (response) => {
        if (response.statusCode === 200 && response.data) {
          // Usar los datos completos del backend
          this.selectedAppointment = response.data;
          console.log('Detalles completos del appointment cargados:', this.selectedAppointment);
          
          // Generar detalles de la reunión usando la nueva lógica
          this.generateMeetingDetails();
          
          this.isLoadingAppointmentDetails = false;
        } else {
          console.error('Error al cargar detalles del appointment:', response.statusCode);
          // Fallback a los datos del appointment original
          this.selectedAppointment = appointment;
          this.generateMeetingDetails();
          this.isLoadingAppointmentDetails = false;
        }
      },
      error: (error) => {
        console.error('Error al cargar detalles del appointment:', error);
        // Fallback a los datos del appointment original
        this.selectedAppointment = appointment;
        this.generateMeetingDetails();
        this.isLoadingAppointmentDetails = false;
      }
    });
  }

  closeMeetingModal(): void {
    this.showMeetingModal = false;
    this.selectedAppointment = null;
    this.isLoadingAppointmentDetails = false;
    // Limpiar los valores generados
    this.meetingId = '';
    this.accessCode = '';
    this.meetingLink = '';
  }

  copyMeetingLink(): void {
    if (this.selectedAppointment && this.meetingLink) {
      navigator.clipboard.writeText(this.meetingLink).then(() => {
        // Mostrar notificación de éxito
        Swal.fire({
          title: '¡Enlace copiado!',
          text: 'El enlace de la reunión se ha copiado al portapapeles',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
          toast: true,
          position: 'top-end'
        });
      }).catch(err => {
        console.error('Error al copiar enlace:', err);
        Swal.fire({
          title: 'Error',
          text: 'No se pudo copiar el enlace',
          icon: 'error',
          confirmButtonText: 'Aceptar'
        });
      });
    }
  }

  sendMeetingEmail(): void {
    if (!this.selectedAppointment || !this.user) {
      console.error('No hay cita seleccionada o usuario disponible');
      return;
    }

    // Mostrar loading
    this.loader.show();

    // Preparar datos del email para el cliente
    const clientEmailData: EmailData = {
      to: this.selectedAppointment.customer?.email || this.selectedAppointment.clientEmail,
      subject: 'Información de tu cita con Phoenix - Detalles de la reunión',
      template: 'appointment-info',
      data: {
        clientName: this.selectedAppointment.customer?.name || this.selectedAppointment.clientName,
        mentorName: this.selectedAppointment.coach?.name || this.user.name,
        sessionDate: this.selectedAppointment.date.toString(),
        sessionTime: this.selectedAppointment.time.toString(),
        meetingId: this.meetingId,
        accessCode: this.getAccessCode(),
        meetingLink: this.meetingLink,
        coachType: this.selectedAppointment.coach?.type?.name || this.user.type?.name
      }
    };

    // Preparar datos del email para el coach
    const coachEmailData: EmailData = {
      to: this.user.email,
      subject: 'Información de tu cita con Phoenix - Detalles de la reunión',
      template: 'appointment-info',
      data: {
        clientName: this.selectedAppointment.customer?.name || this.selectedAppointment.clientName,
        mentorName: this.selectedAppointment.coach?.name || this.user.name,
        sessionDate: this.selectedAppointment.date.toString(),
        sessionTime: this.selectedAppointment.time.toString(),
        meetingId: this.meetingId,
        accessCode: this.getAccessCode(),
        meetingLink: this.meetingLink,
        coachType: this.selectedAppointment.coach?.type?.name || this.user.type?.name
      }
    };

    // Enviar emails tanto al cliente como al coach
    const clientEmailPromise = this.emailService.sendAppointmentConfirmation(clientEmailData).toPromise();
    const coachEmailPromise = this.emailService.sendAppointmentConfirmation(coachEmailData).toPromise();

    // Esperar a que ambos emails se envíen
    Promise.all([clientEmailPromise, coachEmailPromise]).then(([clientResponse, coachResponse]) => {
      this.loader.hide();
      console.log('Emails enviados exitosamente:', { clientResponse, coachResponse });
      
      // Mostrar mensaje de éxito
      Swal.fire({
        title: '¡Emails enviados!',
        text: 'La información de la cita ha sido enviada tanto al cliente como al coach por correo electrónico.',
        icon: 'success',
        confirmButtonText: 'Aceptar',
        customClass: {
          container: 'alert-container',
          confirmButton: 'alert-confirmButton'
        }
      });
    }).catch((error) => {
      this.loader.hide();
      console.error('Error al enviar emails:', error);
      
      // Mostrar mensaje de error
      Swal.fire({
        title: 'Error al enviar emails',
        text: 'No se pudieron enviar los emails. Por favor, intenta nuevamente.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
        customClass: {
          container: 'alert-container',
          confirmButton: 'alert-confirmButton'
        }
      });
    });
  }


  private generateMeetingId(): string {
    // Generar un ID único para la reunión basado en la fecha y hora
    const dateStr = this.selectedAppointment.date.replace(/-/g, '');
    const timeStr = this.selectedAppointment.time.replace(':', '');
    const randomStr = Math.random().toString(36).substring(2, 8);
    return `${dateStr}${timeStr}${randomStr}`;
  }

  getMeetingId(): string {
    if (this.selectedAppointment) {
      return this.selectedAppointment.appointmentId;
    }
    return '';
  }

  generateAccessCode(): string {
    // Generar un código de acceso de 6 dígitos
    return Math.floor(100000 + Math.random() * 900000).toString();
  }

  // Obtener código de acceso generado
  getAccessCode(): string {
    return this.accessCode;
  }

  deleteAppointment(appointment: any): void {
    // Verificar que la cita tenga los datos necesarios
    console.log('Datos de la cita a eliminar:', appointment);
    
    if (!appointment.clientName || !appointment.date || !appointment.time) {
      console.error('Datos de cita incompletos:', appointment);
      Swal.fire({
        title: 'Error',
        text: 'Los datos de la cita están incompletos. No se puede proceder con la eliminación.',
        icon: 'error',
        confirmButtonText: 'Aceptar'
      });
      return;
    }

    // Mostrar confirmación antes de eliminar
    Swal.fire({
      title: '¿Estás seguro?',
      text: `¿Deseas eliminar la cita con ${appointment.clientName} del ${appointment.date} a las ${appointment.time}?\n\nEsta acción eliminará la cita tanto del experto como del cliente.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      customClass: {
        container: 'alert-container',
        confirmButton: 'alert-confirmButton',
        cancelButton: 'alert-cancelButton'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDeleteAppointment(appointment);
      }
    });
  }

  // Función que realiza la eliminación
  private performDeleteAppointment(appointment: any): void {
    // Verificar que tenemos los datos necesarios para identificar la cita
    if (!appointment.date || !appointment.time || !appointment.bookedByUserId) {
      console.error('Datos de cita incompletos:', appointment);
      Swal.fire({
        title: 'Error',
        text: 'No se pudo identificar la cita a eliminar. Faltan datos necesarios.',
        icon: 'error',
        confirmButtonText: 'Aceptar'
      });
      return;
    }

    // Validar que bookedByUserId sea un ObjectId válido (24 caracteres hexadecimales)
    const objectIdRegex = /^[0-9a-fA-F]{24}$/;
    if (!objectIdRegex.test(appointment.bookedByUserId)) {
      console.error('bookedByUserId no es un ObjectId válido:', appointment.bookedByUserId);
      Swal.fire({
        title: 'Error',
        text: 'Los datos de la cita no son válidos. No se puede proceder con la eliminación.',
        icon: 'error',
        confirmButtonText: 'Aceptar'
      });
      return;
    }

    // Mostrar loading
    Swal.fire({
      title: 'Eliminando cita...',
      text: 'Por favor espera',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    // Para las citas del coach, usamos el nuevo endpoint de limpieza
    const coachId = this.user?._id || this.user?.id;
    if (this.user && coachId) {
      console.log('Eliminando cita del coach:', coachId, appointment);
      
      // Primero hacer health check para verificar conectividad
      this.usersService.healthCheck().subscribe({
        next: (healthResponse) => {
          console.log('Health check exitoso:', healthResponse);
          
          // Si el health check funciona, proceder con la eliminación
          const appointmentData = {
            date: appointment.date,
            time: appointment.time,
            bookedByUserId: appointment.bookedByUserId
          };
          
          console.log('Enviando datos de eliminación:', appointmentData);
          
          this.usersService.cleanCoachAppointment(coachId, appointmentData).subscribe({
            next: (response) => {
              console.log('Cita eliminada del coach exitosamente:', response);
              this.removeAppointmentFromLocalLists(appointment);
            },
            error: (error) => {
              console.error('Error al eliminar cita del coach:', error);
              
              // Mostrar mensaje de error específico
              let errorMessage = 'No se pudo eliminar la cita. Por favor, intenta nuevamente.';
              if (error.error?.message) {
                errorMessage = error.error.message;
              } else if (error.message) {
                errorMessage = error.message;
              }
              
              // Si es un error 404, significa que el endpoint no existe
              if (error.status === 404) {
                errorMessage = 'El endpoint de eliminación no está disponible. Por favor, contacta al administrador.';
              }
              
              Swal.fire({
                title: 'Error al eliminar',
                text: errorMessage,
                icon: 'error',
                confirmButtonText: 'Aceptar',
                customClass: {
                  container: 'alert-container',
                  confirmButton: 'alert-confirmButton'
                }
              });
            }
          });
        },
        error: (healthError) => {
          console.error('Error en health check:', healthError);
          Swal.fire({
            title: 'Error de conexión',
            text: 'No se pudo conectar con el servidor. Por favor, verifica que el backend esté funcionando e intenta nuevamente.',
            icon: 'error',
            confirmButtonText: 'Aceptar',
            customClass: {
              container: 'alert-container',
              confirmButton: 'alert-confirmButton'
            }
          });
        }
      });
    } else {
      console.error('No se encontró ID del usuario para eliminar la cita');
      Swal.fire({
        title: 'Error',
        text: 'No se pudo identificar al usuario. No se puede eliminar la cita.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
        customClass: {
          container: 'alert-container',
          confirmButton: 'alert-confirmButton'
        }
      });
    }
  }

  // Función auxiliar para remover la cita de las listas locales
  private removeAppointmentFromLocalLists(appointment: any): void {
    // Remover la cita de las listas locales
    this.citas = this.citas.filter(cita => 
      !(cita.date === appointment.date && 
        cita.time === appointment.time && 
        cita.bookedByUserId === appointment.bookedByUserId)
    );
    
    this.citasFiltradas = this.citasFiltradas.filter(cita => 
      !(cita.date === appointment.date && 
        cita.time === appointment.time && 
        cita.bookedByUserId === appointment.bookedByUserId)
    );

    // Actualizar el usuario local
    if (this.user && this.user.coachAppointments) {
      this.user.coachAppointments = this.user.coachAppointments.filter(cita => 
        !(cita.date === appointment.date && 
          cita.time === appointment.time && 
          cita.bookedByUserId === appointment.bookedByUserId)
      );
      
      // Actualizar el usuario en el servicio de autenticación para mantener consistencia
      this.authService.updateUserData(this.user);
    }
    
    // Recargar las citas para asegurar que la vista se actualice
    this.loadAppointments();
    
    // Mostrar mensaje de éxito
    Swal.fire({
      title: '¡Cita eliminada!',
      text: 'La cita ha sido eliminada exitosamente del experto y del cliente. El espacio ha sido liberado.',
      icon: 'success',
      confirmButtonText: 'Aceptar',
      customClass: {
        container: 'alert-container',
        confirmButton: 'alert-confirmButton'
      }
    });
  }

  // --- Nuevos métodos para el rediseño ---
  
  loadAssignedUsers(): void {
    this.loader.show();
    const authUser = this.authService.getAuthUser();
    
    if (!authUser) {
      this.loader.hide();
      return;
    }

    this.usersService.findCustomersByCoach(authUser.id, this.userSearchTerm).subscribe({
      next: (response) => {
        if (response && response.data) {
          this.assignedUsers = this.transformUsersToDisplay(response.data);
          this.applyUserFilters();
        }
        this.loader.hide();
      },
      error: (error) => {
        console.error('Error al cargar usuarios asignados:', error);
        this.loader.hide();
      }
    });
  }

  private transformUsersToDisplay(users: any[]): UserDisplay[] {
    return users.map(user => ({
      id: user.id || user._id,
      _id: user._id,
      name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.name || 'Usuario sin nombre',
      avatar: this.getUserAvatar(user),
      status: this.mapUserStatus(user.status),
      currentStage: this.calculateUserCurrentStage(user),
      pendingTasks: this.calculatePendingTasks(user),
      nextAppointment: this.getNextAppointment(user),
      progress: this.calculateUserProgressPercentage(user),
      createdAt: user.createdAt || new Date().toISOString(),
      email: user.email || '',
      firstName: user.firstName || '',
      lastName: user.lastName || ''
    }));
  }

  private mapUserStatus(status: any): 'activo' | 'inactivo' | 'pendiente' {
    if (!status) return 'pendiente';
    
    const statusName = typeof status === 'string' ? status : status.name;
    switch (statusName?.toLowerCase()) {
      case 'activo':
      case 'active':
        return 'activo';
      case 'inactivo':
      case 'inactive':
        return 'inactivo';
      default:
        return 'pendiente';
    }
  }

  private calculateUserCurrentStage(user: any): string {
    const currentWeek = this.getCurrentWeek(user);
    
    if (currentWeek >= 1 && currentWeek <= 2) {
      return 'Análisis profundo';
    } else if (currentWeek >= 3 && currentWeek <= 3) {
      return 'Mapa de identidad';
    } else if (currentWeek >= 4 && currentWeek <= 4) {
      return 'Phoenix Session';
    } else if (currentWeek >= 5 && currentWeek <= 5) {
      return 'Visionboard';
    } else if (currentWeek >= 6 && currentWeek <= 6) {
      return 'Phoenix Session';
    } else if (currentWeek >= 7 && currentWeek <= 9) {
      return 'PRD 1';
    } else if (currentWeek >= 10 && currentWeek <= 11) {
      return 'Plan de vida';
    } else if (currentWeek >= 12 && currentWeek <= 12) {
      return 'Graduación';
    } else if (currentWeek > 12) {
      return 'Programa completado';
    } else {
      return 'Iniciando programa';
    }
  }

  private calculatePendingTasks(user: any): number {
    let pendingTasks = 0;
    
    if (!user.profile?.registerQuestionsComplete) pendingTasks++;
    if (!user.profile?.testNowComplete) pendingTasks++;
    if (!user.profile?.testFutureComplete) pendingTasks++;
    if (!user.profile?.onboardingComplete) pendingTasks++;
    
    return pendingTasks;
  }

  private getNextAppointment(user: any): string {
    if (!user || !user._id) {
      return 'No programada';
    }

    // Buscar la próxima cita del usuario
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Filtrar citas futuras del usuario específico
    const futureAppointments = this.sessions.filter(session => {
      // Verificar que la sesión pertenece al usuario
      const sessionBelongsToUser = session.menteeName === `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
                                  session.menteeName === user.name ||
                                  session.menteeName === `${user.firstName || ''} ${user.lastName || ''}`.trim();
      
      if (!sessionBelongsToUser) return false;
      
      const sessionDate = new Date(session.date);
      const sessionDateOnly = new Date(sessionDate.getFullYear(), sessionDate.getMonth(), sessionDate.getDate());
      return sessionDateOnly >= today;
    });

    if (futureAppointments.length === 0) {
      return 'No programada';
    }

    // Ordenar por fecha y tomar la más próxima
    const nextAppointment = futureAppointments.sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    )[0];

    // Formatear la fecha
    const appointmentDate = new Date(nextAppointment.date);
    const formattedDate = appointmentDate.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    return `${formattedDate} ${nextAppointment.time}`;
  }

  private calculateUserProgressPercentage(user: any): number {
    const currentWeek = this.getCurrentWeek(user);
    const progressPerWeek = 100 / 12;
    const calculatedProgress = Math.min(currentWeek * progressPerWeek, 100);
    
    return Math.round(calculatedProgress);
  }

  private getCurrentWeek(user: any): number {
    if (!user?.createdAt) {
      return 1;
    }

    const createdAt = new Date(user.createdAt);
    const now = new Date();
    const diffInMs = now.getTime() - createdAt.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const week = Math.ceil((diffInDays + 1) / 7);
    
    return week;
  }

  private getUserAvatar(user: any): string {
    if (user.personalSection?.photoBase64) {
      return this.formatImageUrl(user.personalSection.photoBase64);
    }
    
    if (user.photo) {
      return this.formatImageUrl(user.photo);
    }
    
    return '/assets/icons/default-avatar.png';
  }

  private formatImageUrl(imageString: string): string {
    if (imageString.startsWith('data:image/')) {
      return imageString;
    }
    
    if (imageString.startsWith('http://') || imageString.startsWith('https://')) {
      return imageString;
    }
    
    if (imageString.startsWith('/')) {
      return imageString;
    }
    
    if (this.isBase64String(imageString)) {
      return `data:image/jpeg;base64,${imageString}`;
    }
    
    return `data:image/jpeg;base64,${imageString}`;
  }

  private isBase64String(str: string): boolean {
    try {
      const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
      if (!base64Regex.test(str)) {
        return false;
      }
      
      if (str.length % 4 !== 0) {
        return false;
      }
      
      const decoded = atob(str);
      const bytes = new Uint8Array(decoded.length);
      for (let i = 0; i < decoded.length; i++) {
        bytes[i] = decoded.charCodeAt(i);
      }
      
      const isJpeg = bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF;
      const isPng = bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47;
      const isGif = bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46;
      
      return isJpeg || isPng || isGif;
    } catch (error) {
      return false;
    }
  }

  loadTodayAppointments(): void {
    // Simular citas del día
    this.todayAppointments = [
      {
        id: 1,
        clientName: 'María González',
        time: '10:00 AM',
        type: 'Sesión individual',
        status: 'Confirmada'
      },
      {
        id: 2,
        clientName: 'Carlos Rodríguez',
        time: '2:00 PM',
        type: 'Sesión grupal',
        status: 'Pendiente'
      }
    ];
  }

  // Métodos para filtrado de usuarios
  onUserSearchChange(): void {
    this.applyUserFilters();
  }

  onUserFilterChange(): void {
    this.applyUserFilters();
  }

  applyUserFilters(): void {
    let filtered = [...this.assignedUsers];

    // Filtro por búsqueda
    if (this.userSearchTerm.trim()) {
      filtered = filtered.filter(user => 
        user.name.toLowerCase().includes(this.userSearchTerm.toLowerCase())
      );
    }

    // Filtro por estado
    if (this.userStatusFilter) {
      filtered = filtered.filter(user => user.status === this.userStatusFilter);
    }

    // Filtro por etapa
    if (this.userStageFilter) {
      filtered = filtered.filter(user => 
        user.currentStage.toLowerCase().replace(/\s+/g, '-') === this.userStageFilter
      );
    }

    this.filteredUsers = filtered;
    this.userCurrentPage = 1;
    this.updateUserPagination();
  }

  // Métodos de ordenamiento para usuarios
  sortUsersBy(field: string): void {
    if (this.userSortField === field) {
      this.userSortDirection = this.userSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.userSortField = field;
      this.userSortDirection = 'asc';
    }

    this.filteredUsers.sort((a, b) => {
      let aValue = a[field as keyof UserDisplay];
      let bValue = b[field as keyof UserDisplay];

      if (aValue === undefined && bValue === undefined) {
        return 0;
      }
      if (aValue === undefined) {
        return this.userSortDirection === 'asc' ? 1 : -1;
      }
      if (bValue === undefined) {
        return this.userSortDirection === 'asc' ? -1 : 1;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return this.userSortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return this.userSortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });

    this.updateUserPagination();
  }

  // Métodos de paginación para usuarios
  updateUserPagination(): void {
    this.userTotalPages = Math.ceil(this.filteredUsers.length / this.userPageSize);
    
    const startIndex = (this.userCurrentPage - 1) * this.userPageSize;
    const endIndex = startIndex + this.userPageSize;
    
    this.paginatedUsers = this.filteredUsers.slice(startIndex, endIndex);
  }

  goToUserPage(page: number): void {
    if (page >= 1 && page <= this.userTotalPages) {
      this.userCurrentPage = page;
      this.updateUserPagination();
    }
  }

  getUserPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, this.userCurrentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(this.userTotalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  }

  onUserPageSizeChange(): void {
    this.userCurrentPage = 1;
    this.updateUserPagination();
  }

  // Métodos para acciones de usuarios
  viewUser(user: any): void {
    // Navigate to user detail page (same as users-assigned)
    this.router.navigate(['/dashboard/users-assigned', user._id]);
  }

  // Método para ver la lista completa de usuarios
  viewAllUsers(): void {
    // Navegar a la página completa de usuarios asignados
    this.router.navigate(['/dashboard/users-assigned']);
  }

  // Método para ver la lista completa de sesiones
  viewAllSessions(): void {
    // Navegar a la página de agenda
    this.router.navigate(['/dashboard/agenda']);
  }

  editUser(user: any): void {
    console.log('Editar usuario:', user);
    // Implementar lógica para editar usuario
  }

  messageUser(user: any): void {
    console.log('Enviar mensaje a:', user);
    // Implementar lógica para enviar mensaje
  }

  addUser(): void {
    console.log('Agregar nuevo usuario');
    // Implementar lógica para agregar usuario
  }

  // Métodos para citas
  openAppointmentModal(appointment: any): void {
    this.selectedAppointment = appointment;
    this.showAppointmentModal = true;
  }

  closeAppointmentModal(): void {
    this.showAppointmentModal = false;
  }


  editAppointment(): void {
    console.log('Editar cita');
    // Implementar lógica para editar cita
  }

  cancelAppointment(): void {
    console.log('Cancelar cita');
    // Implementar lógica para cancelar cita
  }

  // Métodos para filtros y paginación de sesiones
  onSearchChange(): void {
    this.applyFilters();
  }

  onFilterChange(): void {
    this.applyFilters();
  }

  applyFilters(): void {
    let filtered = [...this.sessions];

    // Mostrar únicamente próximas citas (fecha y hora posteriores al momento actual)
    const now = new Date();
    filtered = filtered.filter(session => this.getSessionDateTime(session).getTime() > now.getTime());

    // Filtro por búsqueda
    if (this.searchTerm.trim()) {
      filtered = filtered.filter(session => 
        session.menteeName.toLowerCase().includes(this.searchTerm.toLowerCase())
      );
    }

    // Filtro por estado
    if (this.statusFilter) {
      filtered = filtered.filter(session => session.status === this.statusFilter);
    }

    // Filtro por etapa/milestone
    if (this.stageFilter) {
      filtered = filtered.filter(session => 
        session.milestone?.toLowerCase().includes(this.stageFilter.toLowerCase())
      );
    }

    // Filtro por fecha desde (sobrescribe el filtro por defecto si se especifica)
    if (this.dateFromFilter) {
      const fromDate = new Date(this.dateFromFilter);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(session => {
        const sessionDateTime = this.getSessionDateTime(session);
        return sessionDateTime.getTime() >= fromDate.getTime();
      });
    }

    // Filtro por fecha hasta
    if (this.dateToFilter) {
      const toDate = new Date(this.dateToFilter);
      toDate.setHours(23, 59, 59, 999); // Establecer al final del día
      filtered = filtered.filter(session => {
        const sessionDateTime = this.getSessionDateTime(session);
        return sessionDateTime.getTime() <= toDate.getTime();
      });
    }

    // Ordenar por fecha y hora ascendente para mostrar las próximas citas primero
    filtered.sort((a, b) => this.getSessionDateTime(a).getTime() - this.getSessionDateTime(b).getTime());

    // Mostrar solo las próximas 5 citas
    filtered = filtered.slice(0, 5);

    this.filteredSessions = filtered;
    this.currentPage = 1;
    this.updatePagination();
  }

  sortBy(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }

    this.filteredSessions.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (field) {
        case 'menteeName':
          aValue = a.menteeName.toLowerCase();
          bValue = b.menteeName.toLowerCase();
          break;
        case 'date':
          aValue = new Date(a.date);
          bValue = new Date(b.date);
          break;
        case 'time':
          aValue = a.time;
          bValue = b.time;
          break;
        case 'duration':
          aValue = a.duration;
          bValue = b.duration;
          break;
        case 'milestone':
          aValue = a.milestone.toLowerCase();
          bValue = b.milestone.toLowerCase();
          break;
        default:
          aValue = a.menteeName.toLowerCase();
          bValue = b.menteeName.toLowerCase();
      }

      if (aValue < bValue) return this.sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    this.updatePagination();
  }

  updatePagination(): void {
    this.totalPages = Math.ceil(this.filteredSessions.length / this.pageSize);
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedSessions = this.filteredSessions.slice(startIndex, endIndex);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePagination();
    }
  }

  onPageSizeChange(): void {
    this.currentPage = 1;
    this.updatePagination();
  }

  // Navegar a la agenda
  goToAgenda(): void {
    this.router.navigate(['/dashboard/agenda']);
  }

  enterSession(session: Session): void {
    console.log('Ingresar a sesión:', session);
    
    if (session.status === 'pending') {
      // Para sesiones pendientes, generar enlace de Meet y abrir en nueva pestaña
      const meetingLink = this.generateMeetingLink(session);
      
      if (meetingLink) {
        window.open(meetingLink, '_blank');
      } else {
        this.showAlert('Sesión no disponible', 'La sesión no está disponible aún.', 'warning');
      }
    } else if (session.status === 'completed') {
      // Para sesiones completadas, ver la grabación
      if (session.sessionUrl && session.sessionUrl !== '#') {
        window.open(session.sessionUrl, '_blank');
      } else {
        this.showAlert('Grabación no disponible', 'La grabación no está disponible aún.', 'warning');
      }
    } else {
      this.showAlert('Sesión cancelada', 'Esta sesión ha sido cancelada.', 'error');
    }
  }

  // Método para unirse a la reunión (copiado del dashboard-executive)
  joinMeeting(): void {
    if (this.meetingLink) {
      window.open(this.meetingLink, '_blank');
    }
  }

  // Generar detalles de la reunión (copiado del dashboard-executive)
  private generateMeetingDetails(): void {
    console.log('selectedAppointment', this.selectedAppointment);
    // Usar el ID del appointment como meetingId
    this.meetingId = this.selectedAppointment?._id || this.selectedAppointment?.id || '';
    // Usar la URL del environment
    this.meetingLink = `${environment.meetingUrl}${this.meetingId}`;
  }

  // Obtener enlace de reunión generado (copiado del dashboard-executive)
  getMeetingLink(): string {
    return `${environment.meetingUrl}${this.meetingId.substring(0, 5)}...`;
  }

  // Generar enlace de Meet usando la lógica del agenda
  private generateMeetingLink(session: Session): string {
    if (!session.id) {
      console.error('ID de sesión no disponible');
      return '';
    }
    
    // Usar el ID de la sesión como meetingId, igual que en agenda
    const meetingId = session.id;
    const meetingLink = `${environment.meetingUrl}${meetingId}`;
    
    console.log('🔗 Enlace de Meet generado:', meetingLink);
    return meetingLink;
  }

  // Modal de alerta
  showAlertModal: boolean = false;
  alertTitle: string = '';
  alertMessage: string = '';
  alertType: 'info' | 'warning' | 'error' | 'success' = 'info';

  // Mostrar modal de alerta personalizado
  showAlert(title: string, message: string, type: 'info' | 'warning' | 'error' | 'success' = 'info'): void {
    this.alertTitle = title;
    this.alertMessage = message;
    this.alertType = type;
    this.showAlertModal = true;
  }

  // Cerrar modal de alerta
  closeAlertModal(): void {
    this.showAlertModal = false;
    this.alertTitle = '';
    this.alertMessage = '';
  }

  // Método para cargar sesiones (igual que en agenda)
  loadSessions(): void {
    if (!this.user || !this.user._id) {
      console.warn('No hay usuario autenticado para cargar sesiones');
      this.sessions = [];
      this.applyFilters();
      return;
    }

    this.loaderService.show();

    this.usersService.findCustomersByCoach(this.user._id, '').subscribe({
      next: (response) => {
        this.populateAssignedCustomers(response?.data);
        this.fetchCoachAppointments();
      },
      error: (error) => {
        console.error('Error al cargar clientes asignados:', error);
        this.assignedCustomersMap.clear();
        this.fetchCoachAppointments();
      }
    });
  }

  // Mapear appointment de la base de datos a formato Session (igual que en agenda)
  private mapAppointmentToSession(appointment: any): Session {
    const appointmentDate = this.parseDateString(appointment.date);

    let time12h = appointment.time;
    if (appointment.time && !appointment.time.includes('AM') && !appointment.time.includes('PM')) {
      time12h = this.convertTo12Hour(appointment.time);
    }

    const appointmentCustomer = this.getCustomerForAppointment(appointment);
    
    let milestone = this.getMilestoneForUser(appointmentCustomer);

    let status: 'pending' | 'completed' | 'cancelled' = 'pending';
    const now = new Date();
    const appointmentDateTime = new Date(appointmentDate);
    
    if (appointment.time) {
      const time24h = this.convertTo24Hour(time12h);
      const [hours, minutes] = time24h.split(':');
      appointmentDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    }

    if (appointmentDateTime < now) {
      status = 'completed';
    }

    const files: AppointmentFile[] = Array.isArray(appointment.files)
      ? appointment.files.map((file: any) => ({
          name: file.name,
          size: file.size,
          type: file.type,
          url: file.url,
          uploadDate: file.uploadDate ? new Date(file.uploadDate) : new Date()
        }))
      : [];

    return {
      id: appointment._id || appointment.id || '',
      menteeName: appointmentCustomer?.name || `${appointmentCustomer?.firstName || ''} ${appointmentCustomer?.lastName || ''}`.trim() || 'Cliente',
      menteeAvatar: this.getUserAvatar(appointmentCustomer)  || '/assets/icons/default-avatar.png',
      date: appointmentDate,
      time: time12h,
      duration: appointment.duration || 60,
      milestone: milestone,
      status: status,
      sessionUrl: appointment.meetingUrl || '#',
      notes: appointment.notes || appointment.description || '',
      files
    };
  }

  private populateAssignedCustomers(customers: any[]): void {
    this.assignedCustomersMap.clear();

    if (!Array.isArray(customers)) {
      return;
    }

    customers.forEach(customer => {
      const id = customer?._id || customer?.id;
      if (id) {
        this.assignedCustomersMap.set(id.toString(), customer);
      }
    });
  }

  private fetchCoachAppointments(): void {
    if (!this.user || !this.user._id) {
      this.sessions = [];
      this.applyFilters();
      this.loaderService.hide();
      return;
    }

    this.appointmentsService.findByCoach(this.user._id).subscribe({
      next: (response) => {
        console.log('📅 Citas del coach cargadas:', response);
        
        if (response && response.data && Array.isArray(response.data)) {
          this.sessions = response.data.map(appointment => this.mapAppointmentToSession(appointment));
          console.log('📅 Sesiones mapeadas:', this.sessions);
        } else {
          this.sessions = [];
        }
        
        this.applyFilters();
        this.loaderService.hide();
      },
      error: (error) => {
        console.error('Error al cargar citas del coach:', error);
        this.sessions = [];
        this.applyFilters();
        this.loaderService.hide();
      }
    });
  }

  private getCustomerForAppointment(appointment: any): any {
    if (!appointment) {
      return null;
    }

    const customerId =
      appointment.customer?._id ||
      appointment.customer?.id ||
      appointment.customerId ||
      appointment.customer;

    if (customerId) {
      const customerFromMap = this.assignedCustomersMap.get(customerId.toString());
      if (customerFromMap) {
        return customerFromMap;
      }
    }

    return appointment.customer || null;
  }

  private parseDateString(dateStr: string): Date {
    if (!dateStr) {
      return new Date();
    }

    const trimmed = dateStr.trim();

    if (trimmed.includes('T')) {
      const parsed = new Date(trimmed);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }

    const matches = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (matches) {
      const year = parseInt(matches[1], 10);
      const month = parseInt(matches[2], 10);
      const day = parseInt(matches[3], 10);
      return new Date(year, month - 1, day);
    }

    const parsed = new Date(trimmed);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }

    return new Date();
  }

  // Convertir horario de 12h (AM/PM) a 24h para comparación correcta
  private convertTo24Hour(time12h: string): string {
    if (!time12h) return '00:00';
    
    // Si ya está en formato 24h, devolverlo tal como está
    if (!time12h.includes('AM') && !time12h.includes('PM')) {
      return time12h;
    }
    
    // Extraer hora, minutos y AM/PM
    const timeMatch = time12h.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!timeMatch) {
      console.warn('Formato de hora no reconocido:', time12h);
      return '00:00';
    }
    
    let hour = parseInt(timeMatch[1]);
    const minutes = timeMatch[2];
    const period = timeMatch[3].toUpperCase();
    
    // Convertir a formato 24h
    if (period === 'AM') {
      if (hour === 12) hour = 0; // 12:XX AM = 00:XX
    } else if (period === 'PM') {
      if (hour !== 12) hour += 12; // 1:XX PM = 13:XX, pero 12:XX PM = 12:XX
    }
    
    return `${hour.toString().padStart(2, '0')}:${minutes}`;
  }

  private getSessionDateTime(session: Session): Date {
    const sessionDateTime = new Date(session.date);

    if (session.time) {
      const time24h = this.convertTo24Hour(session.time.trim());
      const [hours, minutes] = time24h.split(':');

      if (!isNaN(parseInt(hours, 10)) && !isNaN(parseInt(minutes, 10))) {
        sessionDateTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
      } else {
        sessionDateTime.setHours(0, 0, 0, 0);
      }
    } else {
      sessionDateTime.setHours(0, 0, 0, 0);
    }

    return sessionDateTime;
  }

  // Convertir formato 24h a 12h para comparación
  private convertTo12Hour(time24h: string): string {
    // Ejemplo: "12:00" -> "12:00 PM", "15:00" -> "3:00 PM"
    const [hours, minutes] = time24h.split(':');
    let hour24 = parseInt(hours, 10);
    let hour12: number;
    let period: string;
    
    if (hour24 === 0) {
      hour12 = 12;
      period = 'AM';
    } else if (hour24 === 12) {
      hour12 = 12;
      period = 'PM';
    } else if (hour24 > 12) {
      hour12 = hour24 - 12;
      period = 'PM';
    } else {
      hour12 = hour24;
      period = 'AM';
    }
    
    // Formatear con padding para mantener consistencia
    const formattedHour = hour12.toString().padStart(2, '0');
    return `${formattedHour}:${minutes} ${period}`;
  }

  // Calcular la semana actual del usuario basado en createdAt
  private getCurrentWeekForUser(user: any): number {
    if (!user?.createdAt) {
      console.log('No createdAt found for user, returning week 1 as default');
      return 1; // Retornar semana 1 por defecto
    }

    const createdAt = new Date(user.createdAt);
    const now = new Date();
    
    console.log('User createdAt:', createdAt);
    console.log('Current date:', now);
    
    // Calcular la diferencia en milisegundos
    const diffInMs = now.getTime() - createdAt.getTime();
    
    // Convertir a días
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    console.log('Days since creation:', diffInDays);
    
    // Calcular la semana (redondeando hacia arriba)
    const week = Math.ceil((diffInDays + 1) / 7);
    
    console.log('Calculated week for user:', week);
    
    return week;
  }

  // Determinar el hito basado en la semana actual del usuario
  private getMilestoneForUser(user: any): string {
    if (!user) {
      return 'Sesión de coaching';
    }

    const currentWeek = this.getCurrentWeekForUser(user);
    console.log('Determining milestone for week:', currentWeek);

    // Mapeo de semanas a hitos (basado en users-assigned-profile.component.ts)
    if (currentWeek >= 1 && currentWeek <= 2) {
      return 'Análisis profundo';
    } else if (currentWeek >= 3 && currentWeek <= 3) {
      return 'Mapa de identidad';
    } else if (currentWeek >= 4 && currentWeek <= 4) {
      return 'Phoenix Session';
    } else if (currentWeek >= 5 && currentWeek <= 5) {
      return 'Visionboard';
    } else if (currentWeek >= 6 && currentWeek <= 6) {
      return 'Phoenix Session';
    } else if (currentWeek >= 7 && currentWeek <= 9) {
      return 'PRD 1';
    } else if (currentWeek >= 10 && currentWeek <= 11) {
      return 'Plan de vida';
    } else if (currentWeek >= 12) {
      return 'Graduación';
    } else {
      return 'Sesión de coaching';
    }
  }

  openNotesModal(session: Session): void {
    this.selectedAppointment = session;
    this.appointmentNotes = session.notes || '';
    this.appointmentFiles = session.files
      ? session.files.map(file => ({
          ...file,
          uploadDate: file.uploadDate ? new Date(file.uploadDate) : new Date()
        }))
      : [];
    this.showPreview = false;
    this.isSavingNotes = false;
    this.showNotesModal = true;

    setTimeout(() => {
      this.initializeQuillEditor();
      if (this.fileInput?.nativeElement) {
        this.fileInput.nativeElement.value = '';
      }
    }, 300);
  }

  closeNotesModal(): void {
    if (this.appointmentFiles.length > 0) {
      this.appointmentFiles.forEach(file => {
        if (file.url && file.url.startsWith('blob:')) {
          URL.revokeObjectURL(file.url);
        }
      });
    }

    this.showNotesModal = false;
    this.isSavingNotes = false;
    this.showPreview = false;
    this.appointmentNotes = '';
    this.appointmentFiles = [];
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
    this.quillInstance = null;
    this.selectedAppointment = null;
  }

  togglePreview(): void {
    this.showPreview = !this.showPreview;
  }

  clearQuillEditor(): void {
    if (this.quillInstance) {
      this.quillInstance.setText('');
    }
    this.appointmentNotes = '';
  }

  triggerFileUpload(): void {
    this.fileInput?.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    Array.from(input.files).forEach(file => this.uploadFile(file));
    input.value = '';
  }

  private uploadFile(file: File): void {
    const fileData: AppointmentFile = {
      name: file.name,
      size: file.size,
      type: file.type,
      uploadDate: new Date(),
      url: URL.createObjectURL(file)
    };

    this.appointmentFiles = [...this.appointmentFiles, fileData];
  }

  getFileIconClass(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();

    switch (extension) {
      case 'pdf':
        return 'fa-file-pdf';
      case 'doc':
      case 'docx':
        return 'fa-file-word';
      case 'ppt':
      case 'pptx':
        return 'fa-file-powerpoint';
      case 'jpg':
      case 'jpeg':
      case 'png':
        return 'fa-file-image';
      case 'txt':
        return 'fa-file-alt';
      default:
        return 'fa-file';
    }
  }

  viewFile(file: AppointmentFile): void {
    if (file.url) {
      window.open(file.url, '_blank');
    }
  }

  deleteFile(index: number): void {
    const [removed] = this.appointmentFiles.splice(index, 1);
    if (removed?.url && removed.url.startsWith('blob:')) {
      URL.revokeObjectURL(removed.url);
    }
    this.appointmentFiles = [...this.appointmentFiles];
  }

  saveAppointmentNotes(): void {
    if (!this.selectedAppointment || !this.selectedAppointment.id) {
      return;
    }

    this.isSavingNotes = true;

    const notesHtml = this.getQuillContent();
    this.appointmentNotes = notesHtml;

    const filesPayload = this.appointmentFiles.map(file => ({
      name: file.name,
      size: file.size,
      type: file.type,
      url: file.url,
      uploadDate: file.uploadDate
    }));

    this.appointmentsService.updateNotesAndFiles(
      this.selectedAppointment.id,
      notesHtml,
      filesPayload
    ).subscribe({
      next: () => {
        this.isSavingNotes = false;
        if (this.selectedAppointment) {
          this.selectedAppointment.notes = notesHtml;
          this.selectedAppointment.files = this.appointmentFiles.map(file => ({ ...file }));
        }
        this.updateSessionCollections(this.selectedAppointment?.id || '', notesHtml, this.appointmentFiles);
        this.closeNotesModal();
        this.showAlert('Notas actualizadas', 'Las notas de la sesión se guardaron correctamente.', 'success');
      },
      error: (error) => {
        console.error('Error al guardar notas:', error);
        this.isSavingNotes = false;
        this.showAlert('Error', 'No se pudieron guardar las notas. Inténtalo de nuevo.', 'error');
      }
    });
  }

  private updateSessionCollections(sessionId: string, notes: string, files: AppointmentFile[]): void {
    const updateCollection = (collection: Session[]) => {
      const index = collection.findIndex(session => session.id === sessionId);
      if (index !== -1) {
        collection[index] = {
          ...collection[index],
          notes,
          files: files.map(file => ({ ...file }))
        };
      }
    };

    updateCollection(this.sessions);
    updateCollection(this.filteredSessions);
    updateCollection(this.paginatedSessions);
    this.paginatedSessions = [...this.paginatedSessions];
  }

  private initializeQuillEditor(): void {
    if (!this.quillEditor) {
      return;
    }

    if (this.quillInstance) {
      this.quillInstance = null;
    }

    const toolbarOptions = [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }],
      ['link', 'image'],
      ['blockquote', 'code-block'],
      ['clean']
    ];

    const options = {
      theme: 'snow',
      placeholder: 'Escriba sus notas aquí...',
      modules: {
        toolbar: toolbarOptions
      },
      formats: [
        'header', 'bold', 'italic', 'underline', 'strike',
        'list', 'bullet', 'align', 'link', 'image',
        'blockquote', 'code-block'
      ]
    };

    setTimeout(() => {
      if (!this.quillEditor) {
        return;
      }

      try {
        this.quillInstance = new Quill(this.quillEditor.nativeElement, options);

        this.quillInstance.root.setAttribute('dir', 'ltr');
        this.quillInstance.root.style.direction = 'ltr';
        this.quillInstance.root.style.textAlign = 'left';

        this.quillInstance.on('text-change', () => {
          this.appointmentNotes = this.quillInstance?.root.innerHTML || '';
        });

        if (this.appointmentNotes) {
          this.quillInstance.root.innerHTML = this.appointmentNotes;
        }
      } catch (error) {
        console.error('Error al inicializar Quill:', error);
      }
    }, 200);
  }

  private getQuillContent(): string {
    return this.quillInstance?.root.innerHTML || this.appointmentNotes || '';
  }

}